import * as React from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import FullCalendar from "@fullcalendar/react";
import type {
  DatesSetArg,
  EventClickArg,
  EventContentArg,
  EventInput,
} from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import { useNavigate } from "react-router-dom";

import { getExternalEvents, getMyBookings, listMeetings } from "@/api/client";
import { BookingWindow } from "@/api/types";
import { CalendarZoom } from "@/components/CalendarZoom";
import { glideCalendar } from "@/lib/calendarGlide";
import { cn } from "@/lib/utils";

import { calendarLinkFor } from "../meetings/shared";
import { bookingLinkFor } from "./links";
import { precisionForRowHeight, snapMinutes, withoutOverlapping } from "./slotEngine";
import { clipWindowToFuture, colorForDay, overlapsBusy } from "./windows";
import "../meetings/fullcalendar-theme.css";

// Hour zoom — same control as the meetings calendar. Row height (rem) per 30-min slot;
// Ctrl+scroll or the −/+ toolbar buttons change it during a visit.
const SLOT_H_MIN = 0.5;
const SLOT_H_MAX = 3.5;
const SLOT_H_STEP = 0.12;
// Fallback height (rem per 30-min slot) used only for the split-second before the calendar has
// measured itself; the auto-fit effect immediately replaces it with a height framed to this
// calendar's own hours.
const DEFAULT_SLOT_H = 1.4;
// The calendar opens showing at LEAST this many hours; if its availability spans more, auto-fit
// zooms OUT so the whole span fits (up to a full day). Framing is computed per calendar from its
// own slots, so one calendar's zoom never leaks into another's (no shared stored value).
const MIN_VIEW_HOURS = 15;
const MAX_VIEW_HOURS = 24;
// Snap grain (minutes) in place mode. Fixed rather than zoom-derived: see minutesAt.
const PLACE_SNAP = 5;

/** Faint shading on alternate two-hour bands, matching the meetings calendar. */
function bandClass(arg: { date?: Date }): string {
  const h = arg.date?.getHours() ?? 0;
  return Math.floor(h / 2) % 2 === 0 ? "fc-band-shade" : "";
}

/**
 * The drag-to-open availability picker.
 *
 * <ul>
 *   <li><b>Click-drag</b> empty time to open ONE free-form slot of exactly the length you drag,
 *       on each day the drag spans (a per-day rectangle, never a continuous overnight block). A
 *       plain <b>click</b> opens one duration-sized slot.</li>
 *   <li>A new/edited slot <b>displaces</b> any hand-drawn slot it overlaps on that day — hand
 *       slots never overlap each other.</li>
 *   <li>Hovering shows a <b>slot-sized cursor</b> (one duration tall) snapped to the grid; the
 *       snap gets finer (down to 1 min) as you zoom in.</li>
 *   <li><b>Drag a slot's bottom edge</b> to lengthen/shorten it; <b>click a slot</b> to remove it.</li>
 *   <li><b>Show my calendar</b> overlays your existing meetings + Google events as faint,
 *       read-only context.</li>
 *   <li><b>Place mode</b> ({@code clickToPlace}, used by polls) reduces all of that to one thing: a
 *       click puts down one slot of exactly the duration, a click on a placed one removes it. No
 *       free-form length, no sideways painting, no grip. A poll proposes ONE duration, so a slot of
 *       any other length is not something the host can mean.</li>
 * </ul>
 *
 * The grid, axis, zoom, month/day/week/biweekly views and the "show my meetings" overlay are all
 * still FullCalendar; only the drawing interaction is a custom pointer layer (see the pointer
 * effect below), which renders per-day highlights instead of FullCalendar's continuous native
 * select highlight.
 */
export function AvailabilityCalendar({
  windows,
  onChange,
  // Formerly the default time band for month-view all-day drags; month-view drag-create was
  // dropped with the new pointer layer (draw in a timegrid view), so these are no longer read.
  defaultFrom: _defaultFrom,
  defaultTo: _defaultTo,
  showMine,
  slotMinutes,
  // Reserved for the deferred "fill from availability pattern" interval tint (see PR follow-ups); the
  // hand-drawn slots this component now draws are free-form and carry no interval.
  intervalMinutes: _intervalMinutes,
  headerLeft,
  headerCenter,
  headerRight,
  headerAbove,
  onRevealMeetings,
  onViewChange,
  onBusyChange,
  biweekly,
  initialDate,
  patternMode,
  topNotice,
  clickToPlace,
}: {
  windows: BookingWindow[];
  onChange: (next: BookingWindow[]) => void;
  defaultFrom: string;
  defaultTo: string;
  showMine: boolean;
  /** Booking duration — each availability block is drawn divided into this-many-minute slots. */
  slotMinutes?: number;
  /** Stride between slot starts (duration + gap). Slots render every this-many minutes, so an
   *  interval larger than the duration shows GAPS between the drawn slots. Defaults to slotMinutes
   *  (contiguous, the ad-hoc case). */
  intervalMinutes?: number;
  /** Optional content shown left-aligned in the toolbar row, opposite the zoom control. */
  headerLeft?: React.ReactNode;
  /** Optional content centred in the toolbar row (between headerLeft and the zoom control). */
  headerCenter?: React.ReactNode;
  /** Optional content right-aligned in the toolbar row, immediately before the zoom control. */
  headerRight?: React.ReactNode;
  /** Optional content shown on its own row ABOVE the toolbar (keeps a long control from
   *  crowding/overlapping the three-column toolbar). */
  headerAbove?: React.ReactNode;
  /** Turn the "Show my confirmed meetings" overlay on — the action on the in-panel clash chip
   *  (which is raised here when a block is drawn over a meeting while the overlay is hidden). */
  onRevealMeetings?: () => void;
  /** Reports the visible view type + date span whenever the calendar navigates, so the builder
   *  can scope Fill/Clear to the current view. */
  onViewChange?: (scope: { view: string; from: string; to: string }) => void;
  /** Reports the host's busy intervals (confirmed meetings / bookings / Google events) for the
   *  visible range, as epoch-ms spans, whenever they load. Lets the builder skip these when filling
   *  from an availability pattern (ad-hoc). Pass a STABLE callback (it fires from the busy-load effect). */
  onBusyChange?: (busy: { start: number; end: number }[]) => void;
  /** Rolling-calendar biweekly mode: open on a Monday-aligned TWO-week grid (one shared time axis,
   *  so both weeks scroll together) with the view switches hidden. Availability drawn here is a
   *  repeating fortnight pattern, not dated. */
  biweekly?: boolean;
  /** Which date the grid opens on (FullCalendar initialDate). Used by biweekly to start on the
   *  current-week Monday so the whole fortnight is drawable. */
  initialDate?: string;
  /** Rolling PATTERN mode: the grid shows a Monday–Sunday week (or fortnight) anchored to the CURRENT
   *  week; the whole week is drawable and UNSHADED even for times earlier today / earlier this week
   *  (a repeating pattern isn't dated), and navigation can't step before this week. Off (ad-hoc) keeps
   *  dated behaviour: past time is hatched and past slots are clipped away. */
  patternMode?: boolean;
  /** A transient toast the caller controls, overlaid just under the weekday header (where the hours
   *  start), so it never reflows the page. The caller owns its content, fade and timing. */
  topNotice?: React.ReactNode;
  /** PLACE mode (polls): one click puts down one slot of exactly {@link slotMinutes}, a click on a
   *  placed one removes it, and that is the whole interaction — no free-form drag, no sideways
   *  painting, no resize grip. A poll has ONE duration for every time it proposes, so a slot of any
   *  other length is not a thing the host can mean. Undefined leaves ad-hoc and rolling untouched. */
  clickToPlace?: boolean;
}) {
  const calRef = React.useRef<FullCalendar | null>(null);
  const boxRef = React.useRef<HTMLDivElement | null>(null);
  // FullCalendar can invoke a click/drag handler with a stale closure; read the live
  // windows through a ref so add/remove always act on the current set, never an old one.
  const windowsRef = React.useRef(windows);
  windowsRef.current = windows;
  // Live values the (mount-once) custom pointer layer reads, so its handlers never close over a
  // stale duration or onChange.
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const slotMinutesRef = React.useRef(slotMinutes);
  slotMinutesRef.current = slotMinutes;
  // Pattern mode lets the pointer layer create slots earlier today / this week (a repeating pattern
  // is not dated), so the "never create a fully-past slot" guard is lifted through this live ref.
  const patternModeRef = React.useRef(patternMode);
  patternModeRef.current = patternMode;
  // Place mode is read through a ref for the same reason as the others: the pointer layer is bound
  // once on mount. Adding it to that effect's dep array would re-bind mid-gesture, dropping the
  // listeners and orphaning the overlay node.
  const clickToPlaceRef = React.useRef(clickToPlace);
  clickToPlaceRef.current = clickToPlace;
  // The pointer effect wires the resize grip up after mount; renderEventContent (which runs on
  // every FullCalendar paint) calls through this ref so the grip always hits the live handler.
  const startResizeRef = React.useRef<
    ((winStart: string, winEnd: string, ev: React.MouseEvent) => void) | null
  >(null);
  const [overlay, setOverlay] = React.useState<EventInput[]>([]);
  const [range, setRange] = React.useState<{ from: string; to: string } | null>(null);
  // Active view type — drives the glide (prev/next) button labels via props (never by poking
  // FullCalendar's DOM, which double-rendered against its internal renderer).
  const [viewType, setViewType] = React.useState<string>("timeGridRolling");
  const [slotH, setSlotH] = React.useState<number>(DEFAULT_SLOT_H);
  // Distance (px) from the top of the calendar box down to where the hour grid starts (below the
  // toolbar + weekday header) — so an overlaid topNotice can sit "just where the hours start".
  const [gridTop, setGridTop] = React.useState(64);
  // Flipped once the host zooms themselves (Ctrl+scroll or the −/+ buttons); after that auto-fit
  // backs off so their choice sticks for the rest of the visit.
  const manualZoomRef = React.useRef(false);
  const navigate = useNavigate();

  // In-panel clash chip: shown when a block is drawn over a meeting while the overlay is hidden.
  // It lives inside the calendar box (which the user is looking at) and slides in/out from the
  // top border, so a long page can't scroll it out of view. `clashMounted` keeps it in the DOM
  // long enough to animate out; `clashOpen` drives the slide/fade.
  const [clashMounted, setClashMounted] = React.useState(false);
  const [clashOpen, setClashOpen] = React.useState(false);
  const clashTimer = React.useRef<number | undefined>(undefined);
  const flashClash = React.useCallback(() => {
    window.clearTimeout(clashTimer.current);
    setClashMounted(true);
    window.requestAnimationFrame(() => setClashOpen(true)); // mount hidden, then slide in
    clashTimer.current = window.setTimeout(() => setClashOpen(false), 5000);
  }, []);
  React.useEffect(() => () => window.clearTimeout(clashTimer.current), []);

  const zoomBy = React.useCallback((delta: number) => {
    manualZoomRef.current = true; // the host is driving now — stop auto-fitting
    setSlotH((h) => Math.max(SLOT_H_MIN, Math.min(SLOT_H_MAX, Number((h + delta).toFixed(2)))));
  }, []);

  React.useEffect(() => {
    calRef.current?.getApi().updateSize();
  }, [slotH]);

  // Ctrl + wheel zooms the hours (capture phase so we beat FullCalendar's scroller).
  // On macOS the OS may swallow Ctrl+scroll — the −/+ buttons are the reliable fallback.
  React.useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      zoomBy(e.deltaY > 0 ? -SLOT_H_STEP : SLOT_H_STEP);
    };
    el.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => el.removeEventListener("wheel", onWheel, { capture: true } as EventListenerOptions);
  }, [zoomBy]);

  React.useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => calRef.current?.getApi().updateSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Measure where the hour grid begins (below the toolbar + weekday header) so a topNotice lands
  // right there. Re-run on navigation (range) and zoom (slotH); a short delay lets layout settle.
  React.useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const measure = () => {
      const scroller = box
        .querySelector<HTMLElement>(".fc-timegrid-slots")
        ?.closest<HTMLElement>(".fc-scroller");
      if (scroller) setGridTop(scroller.getBoundingClientRect().top - box.getBoundingClientRect().top);
    };
    const id = window.setTimeout(measure, 60);
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(box);
    }
    return () => {
      window.clearTimeout(id);
      ro?.disconnect();
    };
  }, [range, slotH]);

  // Pattern mode floor: Monday of the CURRENT week. The pattern can apply to this week and
  // forward, never to a week that's already gone, so navigation can't step before this date.
  // Memoised as a STABLE reference — a fresh {start} literal each render makes FullCalendar
  // recompute its date profile → re-fire datesSet → setState → re-render → infinite loop.
  const patternMinDate = React.useMemo(() => weekStartOf(new Date().toISOString()), []);
  const validRange = React.useMemo(
    () => (patternMode ? { start: patternMinDate } : undefined),
    [patternMode, patternMinDate],
  );

  // Hours from the earliest slot start to the latest slot end across all drawn availability — drives
  // how far the opening view zooms out. Zero when nothing is drawn (falls back to the minimum view).
  const contentSpanHours = React.useMemo(() => {
    let min = 24 * 60;
    let max = 0;
    for (const w of windows) {
      const s = new Date(w.start);
      const e = new Date(w.end);
      const sMin = s.getHours() * 60 + s.getMinutes();
      let eMin = e.getHours() * 60 + e.getMinutes();
      if (eMin <= sMin) eMin = 24 * 60; // a slot running to midnight = end of day
      if (sMin < min) min = sMin;
      if (eMin > max) max = eMin;
    }
    return max > min ? (max - min) / 60 : 0;
  }, [windows]);

  // The grid spans the whole day (so zoom reveals any hour), but land near where the
  // availability actually sits: the earliest window's hour, or 08:00 by default.
  const scrollTime = React.useMemo(() => {
    let h = 8;
    for (const w of windows) {
      const hr = new Date(w.start).getHours();
      if (hr < h) h = hr;
    }
    return `${String(Math.max(0, h)).padStart(2, "0")}:00:00`;
  }, [windows]);

  // Fetch the host's own calendar (meetings + Google events) for the visible range. We fetch
  // even when the overlay is hidden (showMine off) — the calendar just doesn't render it (see
  // the events list) — so we can still detect when a drawn block clashes with a meeting and warn.
  React.useEffect(() => {
    if (!range) {
      setOverlay([]);
      return;
    }
    let cancelled = false;
    // Past context is irrelevant when setting FUTURE availability — only surface events
    // that haven't ended yet, so "Show my calendar" never paints past bookings/meetings.
    const nowMs = Date.now();
    const future = (end: string | undefined) => end != null && Date.parse(end) > nowMs;
    Promise.allSettled([
      getExternalEvents(range.from, range.to),
      listMeetings(),
      getMyBookings(range.from, range.to),
    ]).then(
      ([ext, mtg, bk]) => {
        if (cancelled) return;
        const evs: EventInput[] = [];
        // Google events: translucent slate. Meetings: translucent green — the same
        // read it has on the meetings calendar, just see-through here.
        if (ext.status === "fulfilled") {
          for (const e of ext.value) {
            if (!future(e.end)) continue;
            evs.push({
              id: `ov-ext-${e.id}`,
              start: e.start,
              end: e.end,
              title: e.title,
              editable: false,
              backgroundColor: "rgba(100,116,139,0.28)",
              borderColor: "#64748b",
              textColor: "#334155",
              extendedProps: { overlay: true },
            });
          }
        }
        if (mtg.status === "fulfilled") {
          for (const m of mtg.value) {
            if (m.decided_slot && future(m.decided_slot.end)) {
              evs.push({
                id: `ov-mtg-${m.conversation_id}`,
                start: m.decided_slot.start,
                end: m.decided_slot.end,
                title: m.subject ?? "Meeting",
                editable: false,
                backgroundColor: "rgba(16,185,129,0.30)",
                borderColor: "#10b981",
                textColor: "#065f46",
                // Clickable: this one is a real in-app meeting, so we can open it (focused) on
                // the Meetings calendar. Carry the conversation id (= meeting id) to navigate.
                classNames: ["cursor-pointer"],
                extendedProps: { overlay: true, meetingId: m.conversation_id },
              });
            }
          }
        }
        // Booking-page bookings you've already taken elsewhere — teal, matching the
        // meetings calendar. These are exactly the times the live preview removes.
        if (bk.status === "fulfilled") {
          for (const b of bk.value) {
            if (!future(b.end)) continue;
            evs.push({
              id: `ov-book-${b.id}`,
              start: b.start,
              end: b.end,
              title: b.title,
              editable: false,
              backgroundColor: "rgba(20,184,166,0.28)",
              borderColor: "#14b8a6",
              textColor: "#0f766e",
              // Clickable: open this booking, focused, in the host's booking view.
              classNames: ["cursor-pointer"],
              extendedProps: { overlay: true, bookingPageId: b.bookingPageId, slotStart: b.start },
            });
          }
        }
        setOverlay(evs);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [range]);

  // Busy intervals (confirmed meetings, Google events, existing bookings) from the overlay —
  // exactly the times the live preview subtracts. Kept in a ref so the draw handler can carve
  // them out of a freshly-drawn window without re-subscribing. Only populated while the overlay
  // is shown (showMine); when it's hidden there's nothing loaded to carve against.
  const busyRef = React.useRef<{ start: number; end: number }[]>([]);
  React.useEffect(() => {
    const busy = overlay
      .map((e) => ({ start: Date.parse(String(e.start)), end: Date.parse(String(e.end)) }))
      .filter((iv) => Number.isFinite(iv.start) && Number.isFinite(iv.end) && iv.end > iv.start);
    busyRef.current = busy;
    onBusyChange?.(busy); // let the builder skip these when filling from an availability pattern
  }, [overlay, onBusyChange]);

  // Starting default scroll: centre the "now" line, but clamp so early/late in the
  // day it snaps to the top/bottom edge instead — the opening view shows awareness of
  // where now sits. Runs once, only for a fresh (empty) page; after that the user's
  // own scrolling or their existing slots take over.
  const didAutoScroll = React.useRef(false);
  React.useEffect(() => {
    if (didAutoScroll.current || windows.length > 0 || !range) return;
    const id = window.setTimeout(() => {
      const slots = boxRef.current?.querySelector<HTMLElement>(".fc-timegrid-slots");
      const scroller = slots?.closest<HTMLElement>(".fc-scroller");
      if (!scroller || scroller.scrollHeight <= scroller.clientHeight) return;
      const now = new Date();
      const nowMin = now.getHours() * 60 + now.getMinutes();
      const MIN = 6 * 60; // slotMinTime
      const MAX = 22 * 60; // slotMaxTime
      const maxTop = scroller.scrollHeight - scroller.clientHeight;
      if (nowMin <= MIN) {
        scroller.scrollTop = 0;
      } else if (nowMin >= MAX) {
        scroller.scrollTop = maxTop;
      } else {
        const pxPerMin = scroller.scrollHeight / (MAX - MIN);
        const centred = (nowMin - MIN) * pxPerMin - scroller.clientHeight / 2;
        scroller.scrollTop = Math.max(0, Math.min(centred, maxTop));
      }
      didAutoScroll.current = true;
    }, 120);
    return () => window.clearTimeout(id);
  }, [range, windows.length]);

  // Auto-fit the opening zoom to THIS calendar: show at least MIN_VIEW_HOURS, and if the drawn
  // availability spans more, zoom OUT so the whole span fits (capped at a full day). Runs on mount
  // and on navigation/content change, but backs off once the host zooms manually. Because it reads
  // each calendar's own slots, one calendar's framing never leaks into another's.
  React.useEffect(() => {
    if (manualZoomRef.current || !range) return;
    const id = window.setTimeout(() => {
      if (manualZoomRef.current) return;
      const scroller = boxRef.current
        ?.querySelector<HTMLElement>(".fc-timegrid-slots")
        ?.closest<HTMLElement>(".fc-scroller");
      const avail = scroller?.clientHeight ?? 0;
      if (avail <= 0) return;
      const hours = Math.min(MAX_VIEW_HOURS, Math.max(MIN_VIEW_HOURS, Math.ceil(contentSpanHours)));
      const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const remPer30 = avail / (hours * 2) / rootPx;
      const next = Math.max(SLOT_H_MIN, Math.min(SLOT_H_MAX, Number(remPer30.toFixed(2))));
      setSlotH((h) => (Math.abs(h - next) < 0.005 ? h : next));
    }, 140);
    return () => window.clearTimeout(id);
  }, [range, contentSpanHours]);

  // A single background span from the start of the visible range up to "now",
  // hatched (see fullcalendar-theme.css) so past, unbookable time reads as clearly
  // off-limits — without a heavy black block. Never in pattern mode: a repeating weekly
  // pattern isn't dated, so earlier-today / earlier-this-week is still valid to draw on.
  const pastBg: EventInput[] =
    !patternMode && range && new Date(range.from).getTime() < Date.now()
      ? [
          {
            start: range.from,
            end: new Date().toISOString(),
            display: "background" as const,
            classNames: ["fc-past-hatch"],
          },
        ]
      : [];

  // Each window is drawn from the first still-bookable slot onward — a block that started
  // this morning renders only its from-now slots, and a fully-past block renders nothing.
  // The transparent block holds discrete slot boxes (renderEventContent); the ORIGINAL window
  // rides in extendedProps so clicks/fills act on it by value, never a fragile index.
  const nowMs = Date.now();
  const windowEvents: EventInput[] = windows.flatMap((w, i) => {
    // Pattern mode draws the whole window (the current week's earlier days are still valid); dated
    // (ad-hoc) mode renders each block only from its first still-bookable slot onward.
    const clipped = patternMode ? w : clipWindowToFuture(w, slotMinutes ?? 30, nowMs);
    if (!clipped) return [];
    return [
      {
        id: String(i),
        start: clipped.start,
        end: clipped.end,
        title: "Available",
        display: "block" as const,
        backgroundColor: "transparent",
        borderColor: "transparent",
        // Tagged so CSS can render the placed slot flush (full column width, no border/padding) —
        // the same footprint as the drag cursor, so a placed slot doesn't look smaller than it.
        classNames: ["avail-slot"],
        extendedProps: { winStart: w.start, winEnd: w.end },
      },
    ];
  });
  // Belt-and-suspenders: whatever the source, an availability block ending at/before now must
  // never render. (windowEvents already clips; this guarantees it even if something upstream
  // ever slipped a past window in.) Overlay/past are handled elsewhere.
  const notPast = (e: EventInput) =>
    typeof e.end !== "string" || Date.parse(e.end) > nowMs;
  const events: EventInput[] = [
    ...pastBg,
    ...(patternMode ? windowEvents : windowEvents.filter(notPast)),
    ...(showMine ? overlay : []),
  ];

  // Weeks that contain availability, for the left "jump to week" rail — one click
  // to a selection weeks or months out, instead of tapping ▸ over and over.
  const jumpWeeks = React.useMemo(() => {
    const map = new Map<number, Date>();
    for (const w of windows) {
      const ws = weekStartOf(w.start);
      if (!map.has(ws.getTime())) map.set(ws.getTime(), ws);
    }
    return [...map.values()]
      .sort((a, b) => a.getTime() - b.getTime())
      .map((date) => ({
        key: String(date.getTime()),
        date,
        label: date.toLocaleDateString(undefined, { day: "numeric", month: "short" }),
      }));
  }, [windows]);

  const jumpTo = (date: Date) => calRef.current?.getApi().gotoDate(date);

  // Split the jump targets by where they sit relative to the visible range, so
  // weeks hidden ABOVE the current view stick to the top of the rail and weeks
  // hidden BELOW stick to the bottom — giving the arrows a real up/down meaning.
  const viewStart = range ? new Date(range.from).getTime() : null;
  const viewEnd = range ? new Date(range.to).getTime() : null;
  const aboveWeeks = viewStart == null ? [] : jumpWeeks.filter((w) => w.date.getTime() < viewStart);
  const belowWeeks = viewEnd == null ? [] : jumpWeeks.filter((w) => w.date.getTime() >= viewEnd);

  // ---- Custom pointer layer (the drawing interaction) ------------------------------------------
  //
  // FullCalendar's native `select` draws a single continuous highlight, which for a sideways drag
  // reads as one overnight block spanning the days. We want a per-day rectangle instead, and a
  // hover cursor + edge-resize, so selection is turned off (selectable={false}) and this layer
  // owns the gesture. It reads day/time geometry from FullCalendar's timegrid DOM — the day column
  // is found by X-coordinate hit-test against the `.fc-timegrid-col[data-date]` rects (the pointer
  // often lands on the background slot-lane, which isn't a descendant of the column), and the cursor
  // / drag preview are painted into a transient overlay layer over the grid. Committed windows are
  // still ordinary FullCalendar events (see renderEventContent).
  //
  // Wired once on mount; live duration/onChange/windows come through refs so the handlers stay
  // stable (matching add/removeEventListener) without going stale.
  React.useEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    const DAY_MIN = 24 * 60; // slotMinTime 00:00 → slotMaxTime 24:00 spans the whole column
    type Col = { date: string; rect: DOMRect };

    // Transient overlay layer (hover cursor + drag preview). It sits inside the position:relative
    // calendar box, above the grid, and never intercepts pointer events, so the box's own listeners
    // still see every move. Rects are positioned relative to THIS layer (avoids border/padding math).
    const layer = document.createElement("div");
    layer.style.cssText = "position:absolute;inset:0;z-index:8;pointer-events:none;";
    box.appendChild(layer);

    // Visible day columns (body only — axis/header have no data-date), left→right by date. We hit-test
    // by X because the pointer often lands on FullCalendar's background slot-lane, which is NOT a
    // descendant of the day column, so target.closest('.fc-timegrid-col') returns null there.
    const gridCols = (): Col[] =>
      Array.from(box.querySelectorAll<HTMLElement>(".fc-timegrid-col[data-date]"))
        .map((el) => {
          const frame = el.querySelector<HTMLElement>(".fc-timegrid-col-frame") ?? el;
          return { date: el.getAttribute("data-date") as string, rect: frame.getBoundingClientRect() };
        })
        .sort((a, b) => a.date.localeCompare(b.date));

    const colAtX = (cols: Col[], x: number): Col | null =>
      cols.find((c) => x >= c.rect.left && x < c.rect.right) ?? null;

    // The VISIBLE time-grid area (the scroller viewport), which begins below the weekday/date header
    // and ends at the bottom of the grid. The column frame spans a full 24h and — once scrolled —
    // starts above the header off-screen, so bounding the cursor by the frame lets it leak into the
    // header; bound by this viewport instead so the cursor only shows over actual hour rows.
    const bodyViewport = (): DOMRect | null => {
      const scroller = box
        .querySelector<HTMLElement>(".fc-timegrid-slots")
        ?.closest<HTMLElement>(".fc-scroller");
      return scroller?.getBoundingClientRect() ?? null;
    };

    // Row height (px per 30-min slot) drives the snap precision — finer as the host zooms in.
    const rowPx = (rect: DOMRect) => rect.height / (DAY_MIN / 30);
    const minutesAt = (clientY: number, rect: DOMRect): number => {
      const raw = ((clientY - rect.top) / rect.height) * DAY_MIN;
      // Place mode holds a 5-minute floor instead of following the zoom. The finer snap is right
      // when you are drawing your own availability, but a poll PROPOSES its times to other people:
      // a host zoomed right in would otherwise ship a poll offering 09:07.
      const grain = clickToPlaceRef.current
        ? PLACE_SNAP
        : precisionForRowHeight(rowPx(rect));
      return Math.max(0, Math.min(DAY_MIN, snapMinutes(raw, grain)));
    };
    const pxForMin = (m: number, rect: DOMRect) => (m / DAY_MIN) * rect.height;

    const localYmd = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const isoAt = (ymd: string, minutes: number): string => {
      const [y, mo, da] = ymd.split("-").map(Number);
      const d = new Date(y, mo - 1, da, 0, 0, 0, 0);
      d.setMinutes(minutes);
      return d.toISOString();
    };

    const clearTransient = () => {
      while (layer.firstChild) layer.removeChild(layer.firstChild);
    };

    // Paint a rect over the given column, [fromMin,toMin), positioned relative to the overlay layer.
    const paintRect = (cls: "scheda-cursor" | "scheda-draw", colRect: DOMRect, fromMin: number, toMin: number) => {
      const lr = layer.getBoundingClientRect();
      const el = document.createElement("div");
      el.className = cls;
      el.style.cssText = [
        "position:absolute",
        `left:${colRect.left - lr.left + 1}px`,
        `width:${Math.max(0, colRect.width - 2)}px`,
        `top:${colRect.top + pxForMin(fromMin, colRect) - lr.top}px`,
        `height:${Math.max(6, pxForMin(toMin, colRect) - pxForMin(fromMin, colRect))}px`,
        "pointer-events:none",
        "box-sizing:border-box",
        "border-radius:6px",
        cls === "scheda-cursor"
          ? "border:1.5px dashed rgba(39,121,167,.75);background:rgba(39,121,167,.10)"
          : "border:1px solid rgba(39,121,167,.7);background:rgba(39,121,167,.33)",
      ].join(";");
      layer.appendChild(el);
    };

    // A new/edited hand slot displaces any it overlaps: windows carry absolute timestamps, so two
    // on different days never overlap — comparing epoch-ms via withoutOverlapping is day-scoped for
    // free-form (never-overnight) slots.
    const commit = (additions: BookingWindow[], dropWinKey?: { start: string; end: string }) => {
      let kept = windowsRef.current
        .filter((w) => !(dropWinKey && w.start === dropWinKey.start && w.end === dropWinKey.end))
        .map((w) => ({ start: Date.parse(w.start), end: Date.parse(w.end), w }));
      for (const a of additions) {
        kept = withoutOverlapping(kept, { start: Date.parse(a.start), end: Date.parse(a.end) });
      }
      onChangeRef.current([...kept.map((k) => k.w), ...additions]);
      if (additions.some((w) => overlapsBusy(w, busyRef.current))) flashClash();
    };

    // Free-form span for a gesture: [lo, hi) minutes; a too-short drag (a plain click) becomes one
    // duration-sized slot from where it started.
    const spanMinutes = (startMin: number, endMin: number, rect: DOMRect): [number, number] => {
      const dur = slotMinutesRef.current ?? 30;
      // Place mode: always exactly one duration from where the gesture started. Returning early
      // also holds the drag PREVIEW to a single block, so an accidental drag shows the host what
      // they are actually going to get rather than a rectangle that snaps back on release.
      if (clickToPlaceRef.current) {
        return [startMin, Math.min(DAY_MIN, startMin + dur)];
      }
      const g = precisionForRowHeight(rowPx(rect));
      let lo = Math.min(startMin, endMin);
      let hi = Math.max(startMin, endMin);
      if (hi - lo < g) hi = Math.min(DAY_MIN, lo + dur);
      return [lo, hi];
    };

    // Which day columns a gesture covers. Free-form drags paint every column between where the
    // gesture began and where it is now; place mode collapses that to the starting column alone.
    const spannedCols = (iA: number, iB: number): [number, number] => {
      if (clickToPlaceRef.current) return [iA, iA];
      return iA <= iB ? [iA, iB] : [iB, iA];
    };

    // --- draw (click-drag → one free-form slot per spanned day) -------------------------------
    let draw: { startDate: string; startMin: number } | null = null;
    let resize: { winStart: string; winEnd: string; date: string; startMin: number } | null = null;

    const drawMove = (ev: MouseEvent) => {
      if (!draw) return;
      clearTransient();
      const cols = gridCols();
      if (cols.length === 0) return;
      const endCol =
        colAtX(cols, ev.clientX) ??
        (ev.clientX < cols[0].rect.left ? cols[0] : cols[cols.length - 1]);
      const [lo, hi] = spanMinutes(draw.startMin, minutesAt(ev.clientY, endCol.rect), endCol.rect);
      const iA = cols.findIndex((c) => c.date === draw!.startDate);
      const iB = cols.findIndex((c) => c.date === endCol.date);
      // Place mode never paints sideways: one gesture puts down one time on the day it began.
      const [from, to] = spannedCols(iA, iB);
      for (let i = from; i <= to; i++) paintRect("scheda-draw", cols[i].rect, lo, hi);
    };
    const drawUp = (ev: MouseEvent) => {
      window.removeEventListener("mousemove", drawMove);
      window.removeEventListener("mouseup", drawUp);
      const d = draw;
      draw = null;
      clearTransient();
      if (!d) return;
      const cols = gridCols();
      if (cols.length === 0) return;
      const endCol =
        colAtX(cols, ev.clientX) ??
        (ev.clientX < cols[0].rect.left ? cols[0] : cols[cols.length - 1]);
      const [lo, hi] = spanMinutes(d.startMin, minutesAt(ev.clientY, endCol.rect), endCol.rect);
      if (hi <= lo) return;
      const iA = cols.findIndex((c) => c.date === d.startDate);
      const iB = cols.findIndex((c) => c.date === endCol.date);
      const [from, to] = spannedCols(iA, iB);
      const now = Date.now();
      const additions: BookingWindow[] = [];
      for (let i = from; i <= to; i++) {
        const w = { start: isoAt(cols[i].date, lo), end: isoAt(cols[i].date, hi) };
        // Never create a fully-past slot in dated (ad-hoc) mode; in pattern mode the current week's
        // earlier days are still valid pattern days, so keep them.
        if (patternModeRef.current || Date.parse(w.end) > now) additions.push(w);
      }
      if (additions.length > 0) commit(additions);
    };

    // --- resize (drag a committed slot's bottom edge) -----------------------------------------
    const resizeMove = (ev: MouseEvent) => {
      if (!resize) return;
      clearTransient();
      const col = gridCols().find((c) => c.date === resize!.date);
      if (!col) return;
      const g = precisionForRowHeight(rowPx(col.rect));
      const end = Math.max(resize.startMin + g, minutesAt(ev.clientY, col.rect));
      paintRect("scheda-draw", col.rect, resize.startMin, end);
    };
    const resizeUp = (ev: MouseEvent) => {
      window.removeEventListener("mousemove", resizeMove);
      window.removeEventListener("mouseup", resizeUp);
      const r = resize;
      resize = null;
      clearTransient();
      if (!r) return;
      const col = gridCols().find((c) => c.date === r.date);
      if (!col) return;
      const g = precisionForRowHeight(rowPx(col.rect));
      const end = Math.max(r.startMin + g, minutesAt(ev.clientY, col.rect));
      commit([{ start: r.winStart, end: isoAt(r.date, end) }], { start: r.winStart, end: r.winEnd });
    };

    const startResize = (winStart: string, winEnd: string, ev: React.MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      clearTransient();
      const s = new Date(winStart);
      resize = { winStart, winEnd, date: localYmd(s), startMin: s.getHours() * 60 + s.getMinutes() };
      window.addEventListener("mousemove", resizeMove);
      window.addEventListener("mouseup", resizeUp);
    };
    startResizeRef.current = startResize;

    // --- hover cursor + gesture start ---------------------------------------------------------
    const onHover = (ev: MouseEvent) => {
      if (draw || resize) return; // an active gesture paints its own feedback
      clearTransient();
      const target = ev.target as HTMLElement;
      // Over a slot (or its resize grip) → it shows remove/resize instead of the draw cursor.
      if (target.closest(".fc-event") || target.closest(".scheda-grip")) return;
      const col = colAtX(gridCols(), ev.clientX);
      if (!col) return;
      // Only over the visible hour rows — not the weekday/date header above or the space below the
      // last visible hour — so the cursor never appears outside the hour grid.
      const vp = bodyViewport();
      if (!vp || ev.clientY < vp.top || ev.clientY > vp.bottom) return;
      const t = minutesAt(ev.clientY, col.rect);
      paintRect("scheda-cursor", col.rect, t, Math.min(DAY_MIN, t + (slotMinutesRef.current ?? 30)));
    };
    const onLeave = () => {
      if (!draw && !resize) clearTransient();
    };
    const onDown = (ev: MouseEvent) => {
      if (ev.button !== 0 || draw || resize) return;
      const target = ev.target as HTMLElement;
      // A slot / its grip = remove or resize, not a new draw. Anything outside a day column
      // (axis, header, day/week nav links, month view) is left to FullCalendar.
      if (target.closest(".fc-event") || target.closest(".scheda-grip")) return;
      const col = colAtX(gridCols(), ev.clientX);
      if (!col) return;
      // Don't begin a draw from the weekday/date header or below the last visible hour row.
      const vp = bodyViewport();
      if (!vp || ev.clientY < vp.top || ev.clientY > vp.bottom) return;
      ev.preventDefault();
      clearTransient();
      draw = { startDate: col.date, startMin: minutesAt(ev.clientY, col.rect) };
      window.addEventListener("mousemove", drawMove);
      window.addEventListener("mouseup", drawUp);
    };

    box.addEventListener("mousedown", onDown);
    box.addEventListener("mousemove", onHover);
    box.addEventListener("mouseleave", onLeave);
    return () => {
      box.removeEventListener("mousedown", onDown);
      box.removeEventListener("mousemove", onHover);
      box.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("mousemove", drawMove);
      window.removeEventListener("mouseup", drawUp);
      window.removeEventListener("mousemove", resizeMove);
      window.removeEventListener("mouseup", resizeUp);
      startResizeRef.current = null;
      clearTransient();
      layer.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEventClick = (arg: EventClickArg) => {
    const { winStart, winEnd, meetingId, bookingPageId, slotStart } = arg.event.extendedProps as {
      winStart?: string;
      winEnd?: string;
      meetingId?: string;
      bookingPageId?: string;
      slotStart?: string;
    };
    // A confirmed-meeting overlay: open it, focused, on the Meetings calendar — the same
    // deep link the chat uses when you click a booked meeting. Carry where we came from so
    // the destination shows a "← Back to your booking page" (see BackBar), and the draft
    // (autosaved) brings the half-built page back on return.
    if (meetingId) {
      navigate(calendarLinkFor(meetingId), { state: { from: { label: "your booking page" } } });
      return;
    }
    // A booking overlay: open its booking page with that slot focused.
    if (bookingPageId && slotStart) {
      navigate(bookingLinkFor(bookingPageId, slotStart), {
        state: { from: { label: "your booking page" } },
      });
      return;
    }
    // Only availability blocks carry winStart/winEnd; overlay/past aren't removable. Each window is
    // now one whole free-form slot, so a click removes it entirely — in every view. (The resize
    // grip stops propagation so dragging it never triggers this removal.)
    if (winStart == null || winEnd == null) return;
    onChange(windowsRef.current.filter((w) => !(w.start === winStart && w.end === winEnd)));
  };

  // Slide the whole week across on prev/next (shared with the meetings calendar).
  const glideTo = (dir: "next" | "prev") => {
    const api = calRef.current?.getApi();
    if (api) glideCalendar(api, boxRef.current, dir);
  };

  // prev/next step by the active view's unit (a month in month view), so label them to match.
  const glideUnit =
    viewType === "dayGridMonth"
      ? "month"
      : viewType === "timeGridDay"
        ? "day"
        : viewType === "timeGridBiweek"
          ? "fortnight"
          : "week";

  const renderEventContent = (arg: EventContentArg) => {
    // The past-hatch (and any future background band) is NOT availability — never draw a slot
    // for it, or it fills the whole "range start → now" span with a phantom block.
    if (arg.event.display === "background") return undefined;
    if (arg.event.id.startsWith("ov-")) {
      // Context overlay (your confirmed meetings / bookings / Google events): show a compact
      // translucent label with the summary so it reads as an event, not an empty box. The
      // event's own text colour is inherited; clickable ones open on their calendar.
      return (
        <div className="h-full w-full overflow-hidden px-1 py-0.5 text-[10px] leading-tight">
          <div className="truncate font-semibold">{arg.event.title}</div>
          {arg.timeText ? <div className="truncate opacity-70">{arg.timeText}</div> : null}
        </div>
      );
    }
    const color = colorForDay(arg.event.startStr);
    if (!arg.view.type.startsWith("timeGrid")) {
      // Month / day-grid: a solid, prominent pill so open availability reads as
      // mine — not the faint translucent "Show my calendar" overlay.
      return (
        <div
          className="mx-0.5 truncate rounded px-1 py-0.5 text-[10px] font-semibold"
          style={{ background: color, color: "#334155" }}
        >
          {arg.timeText || "Open"}
        </div>
      );
    }
    // TimeGrid: one free-form slot per window, labelled start–end. Click removes it (via the
    // event's eventClick); the bottom grip resizes it (custom pointer layer). No subdivision into
    // duration boxes and no + fill handle — those belong to the old interaction.
    const startMs = arg.event.start?.getTime() ?? 0;
    const endMs = arg.event.end?.getTime() ?? startMs;
    return (
      <div
        className="relative h-full w-full cursor-pointer overflow-hidden rounded-[5px] px-1 pt-0.5 text-[10px] font-semibold leading-tight hover:brightness-95"
        style={{ background: color, color: "#334155", boxShadow: "inset 0 -2px 3px rgba(0,0,0,.14)" }}
        // Place mode has no resize, so promising one would be a lie. Read straight off the prop
        // rather than a ref: this runs on every FullCalendar paint, so it is already current.
        title={clickToPlace ? "Click to remove" : "Click to remove · drag the bottom edge to resize"}
      >
        {hhmm(startMs)}–{hhmm(endMs)}
        {!clickToPlace && <div
          // The resize grip: mousedown starts an edge-resize (custom pointer layer) and stops
          // propagation so it never triggers the click-to-remove underneath.
          className="scheda-grip absolute inset-x-0 bottom-0 h-2 cursor-ns-resize"
          style={{ background: "rgba(255,255,255,.4)" }}
          title="Drag to resize"
          onMouseDown={(e) =>
            startResizeRef.current?.(
              arg.event.extendedProps.winStart,
              arg.event.extendedProps.winEnd,
              e,
            )
          }
          onClick={(e) => e.stopPropagation()}
        />}
      </div>
    );
  };

  return (
    <div>
      {/* A long control (the show-my-meetings toggle) gets its own row above, so it can't
          crowd or overlap the three-column toolbar. */}
      {headerAbove && <div className="mb-1.5">{headerAbove}</div>}
      {/* Three columns: Fill/Clear left, the summary genuinely centred (equal 1fr rails on both
          sides), zoom right. Stacks on narrow screens. */}
      <div className="mb-1.5 grid grid-cols-1 items-center gap-x-3 gap-y-1.5 sm:grid-cols-[1fr_auto_1fr]">
        <div className="min-w-0 sm:justify-self-start">{headerLeft}</div>
        <div className="min-w-0 justify-self-center">{headerCenter}</div>
        <div className="flex min-w-0 items-center justify-end gap-2 sm:justify-self-end">
          {headerRight}
          <CalendarZoom
            onZoomOut={() => zoomBy(-2 * SLOT_H_STEP)}
            onZoomIn={() => zoomBy(2 * SLOT_H_STEP)}
            atMin={slotH <= SLOT_H_MIN}
            atMax={slotH >= SLOT_H_MAX}
          />
        </div>
      </div>
      <div className="flex items-stretch gap-2">
        {(aboveWeeks.length > 0 || belowWeeks.length > 0) && (
          <div className="flex w-16 shrink-0 flex-col gap-1 py-1">
            <div className="space-y-1 overflow-auto">
              {aboveWeeks.map((wk) => (
                <JumpButton key={wk.key} label={wk.label} dir="up" onClick={() => jumpTo(wk.date)} />
              ))}
            </div>
            <div className="flex-1" />
            <div className="space-y-1 overflow-auto">
              {belowWeeks.map((wk) => (
                <JumpButton key={wk.key} label={wk.label} dir="down" onClick={() => jumpTo(wk.date)} />
              ))}
            </div>
          </div>
        )}
        <div
          ref={boxRef}
          style={{ "--scheda-slot-h": `${slotH}rem` } as React.CSSProperties}
          className={cn(
            "scheda-calendar avail-cal comic-shadow-sm relative h-[700px] min-h-[420px] max-h-[1200px] flex-1 resize-y overflow-hidden rounded-md border-2 border-black bg-white p-3 dark:bg-background",
            biweekly && "avail-cal-biweekly",
          )}
        >
        {/* Clash chip — slides in from the top border of the calendar panel (overflow-hidden clips
            it when parked above), so it's seen even on a long page. */}
        {clashMounted && (
          <div
            onTransitionEnd={(e) => {
              if (!clashOpen && e.propertyName === "transform") setClashMounted(false);
            }}
            className={cn(
              "pointer-events-auto absolute inset-x-2 top-2 z-20 flex items-start justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 shadow-md transition-all duration-300 ease-out dark:border-amber-700/70 dark:bg-amber-950 dark:text-amber-100",
              clashOpen ? "translate-y-0 opacity-100" : "-translate-y-[140%] opacity-0",
            )}
          >
            <span>
              Some of what you drew overlaps a meeting, so those times won't be offered.
              {!showMine && (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={() => {
                      onRevealMeetings?.();
                      setClashOpen(false);
                    }}
                    className="font-semibold underline underline-offset-2 hover:no-underline"
                  >
                    Show my confirmed meetings
                  </button>{" "}
                  to see where.
                </>
              )}
            </span>
            <button
              type="button"
              onClick={() => setClashOpen(false)}
              aria-label="Dismiss"
              title="Dismiss"
              className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-amber-700 hover:bg-amber-100 dark:text-amber-300 dark:hover:bg-amber-900/50"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {/* Caller-owned transient toast (e.g. "Cleared N slots"), overlaid just under the weekday
            header where the hours start — absolute, so it never reflows the page. */}
        {topNotice && (
          <div
            className="pointer-events-none absolute inset-x-0 z-20 flex justify-center px-2"
            style={{ top: gridTop + 6 }}
          >
            {topNotice}
          </div>
        )}
        <FullCalendar
          ref={calRef}
          plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
          initialView={biweekly ? "timeGridBiweek" : patternMode ? "timeGridWeek" : "timeGridRolling"}
          initialDate={initialDate}
          // Pattern mode: can't navigate before the current week (its start is the pattern floor).
          // Stable reference (memoised) — see the validRange note above re: infinite datesSet loop.
          validRange={validRange}
          views={{
            // A rolling 7-day week that starts on TODAY, so the whole visible grid is
            // bookable — no dead past-space when today is late in a Mon–Sun week.
            timeGridRolling: {
              type: "timeGrid",
              duration: { days: 7 },
              dateAlignment: "day",
              // Without this FullCalendar defaults the step to one day (a single unit of the
              // largest denomination), so prev/next would crawl a day at a time; step a full week.
              dateIncrement: { days: 7 },
              buttonText: "From today",
            },
            // Rolling-calendar biweekly: a Monday-aligned 14-day grid (Week A + Week B in one grid,
            // so a single vertical time axis scrolls both weeks together); glide moves a fortnight.
            timeGridBiweek: {
              type: "timeGrid",
              duration: { days: 14 },
              dateAlignment: "week",
              dateIncrement: { days: 14 },
              buttonText: "Two weeks",
            },
          }}
          customButtons={{
            // Labelled, not icon-only, so it's obvious these step a whole week (the rolling view
            // shows today→+7; "Next week ›" reveals the days beyond the right edge).
            glidePrev: { text: `‹ Prev ${glideUnit}`, click: () => glideTo("prev") },
            glideNext: { text: `Next ${glideUnit} ›`, click: () => glideTo("next") },
            // One button in place of the old separate "Today" + "Day": jumps to today in day view.
            // (Any other day's day view is still one click on its date.)
            todayDay: {
              text: "Today",
              click: () => calRef.current?.getApi().changeView("timeGridDay", new Date()),
            },
          }}
          headerToolbar={{
            left: "glidePrev,glideNext",
            center: "title",
            // Pattern mode (weekly or biweekly) is a fixed Mon–Sun template — hide the month/week/day
            // switches so it can't be knocked into a view where the pattern week no longer lines up.
            right: biweekly || patternMode ? "" : "dayGridMonth,timeGridRolling,timeGridWeek,todayDay",
          }}
          buttonText={{ today: "Today", month: "Month", week: "Mon–Sun", day: "Day" }}
          firstDay={1}
          allDaySlot={false}
          nowIndicator
          // Click a day (its number, or an empty month cell) to jump into that day; the thin
          // › by each week opens that week. navLinks powers both, without touching drag-select.
          navLinks
          weekNumbers
          navLinkDayClick="timeGridDay"
          navLinkWeekClick="timeGridWeek"
          weekNumberContent={() => "›"}
          dateClick={(arg) => {
            if (arg.view.type === "dayGridMonth") {
              arg.view.calendar.changeView("timeGridDay", arg.date);
            }
          }}
          height="100%"
          scrollTime={scrollTime}
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          slotDuration="00:30:00"
          slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
          eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
          slotLaneClassNames={bandClass}
          editable={false}
          // Native drag-select is OFF: the custom pointer layer (see the pointer effect above)
          // owns drawing so it can paint a per-day rectangle instead of FullCalendar's single
          // continuous overnight highlight, plus a hover cursor and edge-resize.
          selectable={false}
          datesSet={(arg: DatesSetArg) => {
            setRange({ from: arg.start.toISOString(), to: arg.end.toISOString() });
            setViewType(arg.view.type);
            // A view/date change re-lays the grid — drop any leftover draw/cursor nodes so they
            // can't linger against the new columns.
            boxRef.current
              ?.querySelectorAll(".scheda-cursor, .scheda-draw")
              .forEach((n) => n.remove());
            // Report the visible view + span so the builder can scope its Fill/Clear actions
            // to whatever the host is currently looking at (this week, this month, …).
            onViewChange?.({
              view: arg.view.type,
              from: arg.start.toISOString(),
              to: arg.end.toISOString(),
            });
          }}
          events={events}
          eventContent={renderEventContent}
          eventClick={handleEventClick}
          eventDidMount={(info) => {
            if (info.event.extendedProps?.meetingId) {
              info.el.title = "Open this meeting on your calendar";
            } else if (info.event.extendedProps?.bookingPageId) {
              info.el.title = "Open this booking";
            }
          }}
        />
        </div>
      </div>
      {/* Place mode drops this line entirely: it says "drag", which is not what happens there, and
          the slot-sized dashed cursor already shows what a click will do. */}
      {!clickToPlace && (
      <p className="mt-2 flex items-center gap-1.5 text-sm font-medium text-foreground">
        {/* A hand resting on a mouse — inline SVG (not an emoji) so it has a real, theme-aware
            outline that stays visible on any background; the drag-hint wiggle animates it. */}
        <svg
          className="drag-hint-hand h-5 w-5 shrink-0 text-teal-700 dark:text-teal-300"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.7}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {/* mouse */}
          <rect x="7" y="8" width="10" height="13" rx="5" />
          <path d="M12 8v3" />
          {/* hand draped over the top of the mouse */}
          <path d="M6 11C6 6.6 8.7 3.6 12 3.6S18 6.6 18 11" />
          <path d="M9 4.7V8.4" />
          <path d="M12 3.7V9" />
          <path d="M15 4.7V8.4" />
        </svg>
        Drag to build slots on the calendar or handpick from below.
      </p>
      )}
    </div>
  );
}

/** One entry in the week-jump rail: a date + an up/down arrow for its direction. */
function JumpButton({
  label,
  dir,
  onClick,
}: {
  label: string;
  dir: "up" | "down";
  onClick: () => void;
}) {
  const Icon = dir === "up" ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${dir === "up" ? "Earlier" : "Later"}: week of ${label}`}
      className="flex w-full items-center gap-0.5 rounded border-2 border-black bg-background px-1 py-1 text-xs font-medium hover:bg-muted"
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}

/** "HH:mm" (24h) for an epoch-ms instant, in the viewer's local zone. */
function hhmm(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** Monday (local midnight) of the ISO week containing {@code iso}. */
function weekStartOf(iso: string): Date {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - dow);
  return d;
}
